/**
 * Window Manager Context
 * Manages all open OS windows: open, close, focus, minimize, maximize, drag, resize
 * Window state persisted to localStorage — survives page refresh
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { OSWindowState, WindowManagerContextValue } from './types';

const WindowManagerContext = createContext<WindowManagerContextValue | null>(null)

let zCounter = 100

// Serializable window state for localStorage
interface PersistedWindow {
  id: string
  title: string
  position: { x: number; y: number }
  size: { width: number; height: number }
  minimized: boolean
  maximized: boolean
  noPadding?: boolean
  /** The registry path used to open this window (from dockConfig) */
  registryPath: string
}

const STORAGE_KEY = 'runai-window-states'

function loadPersistedWindows(): PersistedWindow[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function savePersistedWindows(wins: OSWindowState[]) {
  try {
    const serializable: PersistedWindow[] = wins.map(w => ({
      id: w.id,
      title: w.title,
      position: w.position,
      size: w.size,
      minimized: w.minimized,
      maximized: w.maximized,
      noPadding: w.noPadding,
      registryPath: (w as any)._registryPath || w.id,
    }))
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable))
  } catch { /* quota exceeded etc */ }
}

// Bos alan bul — varolan pencerelere ortusmemeye calis, max 12 kademe ofset dene
function findEmptySlot(
  open: OSWindowState[],
  desired: { x: number; y: number },
  size: { width: number; height: number },
): { x: number; y: number } {
  const vw = window.innerWidth
  const vh = window.innerHeight
  // Available area icin kenardan margin (menubar + dock icin)
  const minX = 12, minY = 40, maxX = vw - size.width - 12, maxY = vh - size.height - 100
  const visible = open.filter(w => !w.minimized && !w.maximized)

  const overlapArea = (ax: number, ay: number) => {
    let total = 0
    for (const w of visible) {
      const dx = Math.max(0, Math.min(ax + size.width, w.position.x + w.size.width) - Math.max(ax, w.position.x))
      const dy = Math.max(0, Math.min(ay + size.height, w.position.y + w.size.height) - Math.max(ay, w.position.y))
      total += dx * dy
    }
    return total
  }

  const clamp = (x: number, y: number) => ({
    x: Math.min(Math.max(x, minX), Math.max(minX, maxX)),
    y: Math.min(Math.max(y, minY), Math.max(minY, maxY)),
  })

  // Once arzu edilen yer
  const first = clamp(desired.x, desired.y)
  if (overlapArea(first.x, first.y) < size.width * size.height * 0.15) return first

  // Cascade dene
  let best = first
  let bestOverlap = overlapArea(first.x, first.y)
  const step = 30
  for (let i = 1; i <= 12; i++) {
    const cand = clamp(desired.x + i * step, desired.y + i * step)
    const ov = overlapArea(cand.x, cand.y)
    if (ov < bestOverlap) { best = cand; bestOverlap = ov }
    if (ov === 0) return cand
  }
  // Grid tara — 4 kose + ortalar
  const candidates = [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: minX, y: maxY },
    { x: maxX, y: maxY },
    { x: Math.floor((minX + maxX) / 2), y: minY },
    { x: minX, y: Math.floor((minY + maxY) / 2) },
    { x: maxX, y: Math.floor((minY + maxY) / 2) },
    { x: Math.floor((minX + maxX) / 2), y: maxY },
  ]
  for (const c of candidates) {
    const ov = overlapArea(c.x, c.y)
    if (ov < bestOverlap) { best = c; bestOverlap = ov }
    if (ov === 0) return c
  }
  return best
}

export function WindowManagerProvider({ children }: { children: React.ReactNode }) {
  const [windows, setWindows] = useState<OSWindowState[]>([])
  const restoredRef = useRef(false)

  // Persist to localStorage on every window state change
  const persistWindows = useCallback((wins: OSWindowState[]) => {
    savePersistedWindows(wins)
  }, [])

  // Restore windows from localStorage on first mount
  useEffect(() => {
    if (restoredRef.current) return
    restoredRef.current = true

    const saved = loadPersistedWindows()
    if (saved.length === 0) return

    // Lazy import registry + dock config to rebuild components
    import('./windowRegistry').then(({ WINDOW_REGISTRY }) => {
        const restored: OSWindowState[] = []
        for (const pw of saved) {
          // Find the component from registry
          const registryPath = pw.registryPath || pw.id
          const Component = WINDOW_REGISTRY[registryPath]
          if (!Component) continue

          zCounter++
          restored.push({
            id: pw.id,
            title: pw.title,
            component: Component as any,
            position: pw.position,
            size: pw.size,
            zIndex: zCounter,
            minimized: pw.minimized,
            maximized: pw.maximized,
            noPadding: pw.noPadding,
            _registryPath: registryPath,
          } as any)
        }
        if (restored.length > 0) {
          setWindows(restored)
        }
    })
  }, [])

  const openWindow = useCallback((config: Omit<OSWindowState, 'zIndex' | 'minimized' | 'maximized'> & { _registryPath?: string }) => {
    setWindows(prev => {
      // Already open? Focus it
      const existing = prev.find(w => w.id === config.id)
      if (existing) {
        zCounter++
        const next = prev.map(w => w.id === config.id
          ? { ...w, minimized: false, zIndex: zCounter }
          : w
        )
        persistWindows(next)
        return next
      }
      zCounter++
      const smartPos = findEmptySlot(prev, config.position, config.size)
      const next = [...prev, { ...config, position: smartPos, zIndex: zCounter, minimized: false, maximized: false }]
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  // 🔴 KURAL 18: runai:open-window event listener — Launchpad, widget'lar, SideChat bu event'i kullanır
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      // detail string veya { windowId } olabilir
      let path = typeof detail === 'string' ? detail : detail?.windowId || detail?.id
      if (!path) return

      // run-sheet, run-docs, run-slides → run-office (tek Univer instance, React #185 önleme)
      const officeAlias: Record<string, string> = { 'run-sheet': 'run-office', 'run-docs': 'run-office', 'run-slides': 'run-office' }
      const resolvedPath = officeAlias[path] ?? path

      import('./windowRegistry').then(({ WINDOW_REGISTRY }) => {
        const Component = WINDOW_REGISTRY[resolvedPath]
        if (!Component) return

        // dockConfig'den boyut bilgisi al (opsiyonel)
        import('../Dock/dockConfig').then(({ DOCK_ITEMS }) => {
          const dockItem = DOCK_ITEMS.find(d => d.path === resolvedPath)
          const width = dockItem?.windowWidth || 860
          const height = dockItem?.windowHeight || 600
          const title = dockItem?.windowTitle || dockItem?.label || resolvedPath
          const noPadding = dockItem?.noPadding || resolvedPath === 'run-cut' || resolvedPath === 'run-office' || resolvedPath === 'run-sheet' || resolvedPath === 'run-docs' || resolvedPath === 'run-canvas' || resolvedPath === 'run-code' || resolvedPath === 'run-browser' || resolvedPath === 'run-terminal' || resolvedPath === 'run-music' || resolvedPath === 'run-studio' || resolvedPath === 'run-player' || resolvedPath === 'run-files' || resolvedPath === 'run-image' || resolvedPath === 'run-video' || false
          const vw = window.innerWidth
          const vh = window.innerHeight

          openWindow({
            id: resolvedPath,
            title,
            component: Component as any,
            position: { x: Math.max(40, (vw - width) / 2), y: Math.max(40, (vh - height) / 2) },
            size: { width, height },
            noPadding,
            _registryPath: resolvedPath,
          } as any)
        })
      })
    }

    window.addEventListener('runai:open-window', handler)
    return () => window.removeEventListener('runai:open-window', handler)
  }, [openWindow])

  const closeWindow = useCallback((id: string) => {
    setWindows(prev => {
      const next = prev.filter(w => w.id !== id)
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const focusWindow = useCallback((id: string) => {
    setWindows(prev => {
      zCounter++
      const next = prev.map(w => w.id === id ? { ...w, zIndex: zCounter } : w)
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const minimizeWindow = useCallback((id: string) => {
    setWindows(prev => {
      const next = prev.map(w => w.id === id ? { ...w, minimized: true } : w)
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const maximizeWindow = useCallback((id: string) => {
    setWindows(prev => {
      const next = prev.map(w => w.id === id ? { ...w, maximized: true } : w)
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const restoreWindow = useCallback((id: string) => {
    setWindows(prev => {
      zCounter++
      const next = prev.map(w => w.id === id ? { ...w, minimized: false, maximized: false, zIndex: zCounter } : w)
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const pinWindow = useCallback((id: string, corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'left-half' | 'right-half') => {
    setWindows(prev => {
      const next = prev.map(w => w.id === id ? { ...w, pinned: corner, maximized: false } : w)
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const unpinWindow = useCallback((id: string) => {
    setWindows(prev => {
      const next = prev.map(w => w.id === id ? { ...w, pinned: null } : w)
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const minimizeAll = useCallback(() => {
    setWindows(prev => {
      const next = prev.map(w => ({ ...w, minimized: true }))
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const restoreAll = useCallback(() => {
    setWindows(prev => {
      const next = prev.map(w => ({ ...w, minimized: false }))
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const updatePosition = useCallback((id: string, pos: { x: number; y: number }) => {
    setWindows(prev => {
      const next = prev.map(w => w.id === id ? { ...w, position: pos } : w)
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const updateSize = useCallback((id: string, size: { width: number; height: number }) => {
    setWindows(prev => {
      const next = prev.map(w => w.id === id ? { ...w, size } : w)
      persistWindows(next)
      return next
    })
  }, [persistWindows])

  const activeWindowId = windows
    .filter(w => !w.minimized)
    .sort((a, b) => b.zIndex - a.zIndex)[0]?.id || null

  // Agent OS control events — window management
  useEffect(() => {
    const closeHandler = (e: Event) => { const id = (e as CustomEvent).detail; if (id) closeWindow(id) }
    const minimizeHandler = (e: Event) => { const id = (e as CustomEvent).detail; if (id) minimizeWindow(id) }
    const maximizeHandler = (e: Event) => { const id = (e as CustomEvent).detail; if (id) maximizeWindow(id) }
    const focusHandler = (e: Event) => { const id = (e as CustomEvent).detail; if (id) focusWindow(id) }
    const pinHandler = (e: Event) => {
      const { id, corner } = (e as CustomEvent).detail || {}
      if (id) pinWindow(id, corner || 'top-right')
    }
    window.addEventListener('runai:close-window', closeHandler)
    window.addEventListener('runai:minimize-window', minimizeHandler)
    window.addEventListener('runai:maximize-window', maximizeHandler)
    window.addEventListener('runai:focus-window', focusHandler)
    window.addEventListener('runai:pin-window', pinHandler)
    return () => {
      window.removeEventListener('runai:close-window', closeHandler)
      window.removeEventListener('runai:minimize-window', minimizeHandler)
      window.removeEventListener('runai:maximize-window', maximizeHandler)
      window.removeEventListener('runai:focus-window', focusHandler)
      window.removeEventListener('runai:pin-window', pinHandler)
    }
  }, [closeWindow, minimizeWindow, maximizeWindow, focusWindow, pinWindow])

  return (
    <WindowManagerContext.Provider value={{
      windows, activeWindowId, openWindow, closeWindow, focusWindow,
      minimizeWindow, maximizeWindow, restoreWindow,
      minimizeAll, restoreAll,
      pinWindow, unpinWindow,
      updatePosition, updateSize,
    }}>
      {children}
    </WindowManagerContext.Provider>
  )
}

export function useWindowManager() {
  const ctx = useContext(WindowManagerContext)
  if (!ctx) { throw new Error('useWindowManager must be inside WindowManagerProvider') }
  return ctx
}
